// Site support: explicit clicks only, durable retry key per game, no automatic creation after login.
import { el as h } from './fs-ui.js';
import { t } from './fs-i18n.js';
/* Jeux que le thème sait afficher ; seuls ceux que le service annonce (GET /features et /me → supportGames) sont proposés,
   moins ceux que le thème ferme au public (`closed`, data-fs-support-closed de fs-support.liquid, réglage
   fs_darkorbit_live). DarkOrbit n'apparaît donc qu'une fois ouvert des deux côtés. */
export const SUPPORT_GAMES = Object.freeze(['waruniverse', 'redgalaxy', 'darkorbit', 'novahorizon']);
const GAME_NAMES = Object.freeze({ waruniverse: 'WarUniverse', redgalaxy: 'Red Galaxy', darkorbit: 'DarkOrbit', novahorizon: 'Nova Horizon' });
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const SUPPORT_REQUEST_PREFIX = 'fs-support-request:';
export function supportGames(value) { return Array.isArray(value) ? SUPPORT_GAMES.filter(game => value.includes(game)) : []; }
export function supportTicket(row) {
  if (!row || row.kind !== 'support' || !SUPPORT_GAMES.includes(row.game) || !['open', 'pending', 'closed'].includes(row.state) ||
    !/^\d{17,20}$/.test(row.channelId ?? '') || !/^[1-9]\d{0,19}$/.test(String(row.number ?? '')) ||
    !['string', 'number'].includes(typeof row.number) || (typeof row.number === 'number' && !Number.isSafeInteger(row.number)) ||
    row.reference !== `T${String(row.number).padStart(6, '0')}`) return null;
  try {
    const url = new URL(row.url);
    if (url.protocol !== 'https:' || url.host !== 'discord.com' || url.username || url.password || url.search || url.hash ||
      !new RegExp(`^/channels/[0-9]{17,20}/${row.channelId}$`).test(url.pathname)) return null;
    return { channelId: row.channelId, number: String(row.number), reference: row.reference, game: row.game, kind: 'support', state: row.state, url: url.href };
  } catch { return null; }
}
export function supportRequest(storage, game, randomUUID = () => crypto.randomUUID()) {
  if (!SUPPORT_GAMES.includes(game)) throw Error('invalid_support_request');
  const key = SUPPORT_REQUEST_PREFIX + game;
  const existing = storage.getItem(key);
  if (UUID.test(existing ?? '')) return existing;
  const requestId = randomUUID();
  if (!UUID.test(requestId)) throw Error('storage');
  storage.setItem(key, requestId);
  if (storage.getItem(key) !== requestId) throw Error('storage');
  return requestId;
}

export function createSupportController({ account, storage, randomUUID, closed = [], onChange = () => {} }) {
  const offered = value => supportGames(value).filter(game => !closed.includes(game));
  let state = { visible: false, games: [], tickets: [], loggedIn: false, loading: false, sending: false, error: '' }, generation = 0;
  const publish = patch => { state = { ...state, ...patch }; onChange(structuredClone(state)); return state; };
  /* Connexion (key), inchangée par un renouvellement du jeton (fs-account.js). */
  const currentSession = () => { const session = account.session(); return session?.key ?? session?.token ?? ''; };
  async function refresh() {
    const run = ++generation, token = currentSession();
    publish({ visible: false, tickets: [], loggedIn: Boolean(token), loading: true, sending: false, error: '' });
    try {
      const publicReply = await account.call('/features');
      if (run !== generation || token !== currentSession()) return;
      if (publicReply.status !== 200 || publicReply.body?.supportTickets !== true) return publish({ visible: false, loading: false });
      let games = offered(publicReply.body.supportGames);
      if (!token) return publish({ visible: games.length > 0, games, loading: false });
      const me = await account.me();
      if (run !== generation || token !== currentSession()) return;
      if (me?.supportTickets !== true) return publish({ visible: false, loading: false });
      games = offered(me.supportGames).filter(game => games.includes(game));
      const replies = await Promise.allSettled(games.map(game => account.call(`/tickets/support?game=${game}`)));
      if (run !== generation || token !== currentSession()) return;
      const tickets = []; let error = '';
      replies.forEach((result, index) => {
        if (result.status !== 'fulfilled' || result.value.status !== 200) {
          const code = result.status === 'fulfilled' ? result.value.body?.error : null;
          error = code === 'ticket_creation_pending' && error !== 'unavailable' ? 'pending' : 'unavailable'; return;
        }
        if (result.value.body?.ticket === null) return;
        const ticket = supportTicket(result.value.body?.ticket);
        if (!ticket || ticket.game !== games[index]) { error = 'unavailable'; return; }
        tickets.push(ticket);
      });
      return publish({ visible: games.length > 0, games, tickets, loading: false, error });
    } catch { if (run === generation && token === currentSession()) publish({ loading: false, error: 'unavailable' }); }
  }
  async function submit(game) {
    if (state.sending || !state.visible || !state.games.includes(game)) return;
    if (!currentSession()) {
      if (!account.login({ kind: 'account', game, returnTo: 'contact' })) publish({ error: 'storage' });
      return;
    }
    const token = currentSession(), run = generation;
    publish({ sending: true, error: '' });
    try {
      let requestId;
      try { requestId = supportRequest(storage, game, randomUUID); } catch { throw Error('storage'); }
      const reply = await account.call('/tickets/support', { method: 'POST', body: { game, requestId } });
      if (run !== generation || token !== currentSession()) return;
      if (reply.status !== 200 && reply.status !== 201) {
        const code = reply.body?.error?.code ?? reply.body?.error;
        throw Error(['ticket_creation_pending', 'busy', 'support_pending'].includes(code) ? 'pending' : reply.status === 429 ? 'rate_limited' : 'unavailable');
      }
      const ticket = supportTicket(reply.body);
      if (!ticket || ticket.game !== game) throw Error('unavailable');
      // Only a positively validated response completes this intention. Ambiguous replies retain the UUID.
      storage.removeItem(SUPPORT_REQUEST_PREFIX + game);
      publish({ tickets: [ticket, ...state.tickets.filter(row => row.channelId !== ticket.channelId)], error: '' });
    } catch (error) {
      if (run === generation && token === currentSession()) publish({ error: ['storage', 'pending', 'rate_limited'].includes(error.message) ? error.message : 'unavailable' });
    } finally { if (run === generation) publish({ sending: false }); }
  }
  return Object.freeze({ refresh, submit, state: () => structuredClone(state) });
}

export function mountSupport(root, account, storage) {
  if (!storage) { try { storage = window.sessionStorage; } catch { storage = { getItem() { throw Error('storage'); } }; } }
  if (!root || root.dataset.fsSupportMounted || !account) return;
  root.dataset.fsSupportMounted = 'true';
  const title = h('h2', { class: 'fs-info-h3', id: 'fs-support-title' }, t('support.title'));
  const status = h('p', { role: 'status', 'aria-live': 'polite', class: 'fs-page-hint' });
  const select = h('select', { id: 'fs-support-game', class: 'fs-select fs-select--native', required: true });
  const button = h('button', { type: 'submit', class: 'fs-btn fs-btn--primary' });
  const list = h('ul', { class: 'fs-page-lines', 'aria-label': t('support.your_tickets') });
  const form = h('form', { class: 'fs-page-form', onsubmit: event => { event.preventDefault(); void controller.submit(select.value); } },
    h('div', { class: 'fs-page-field' }, h('label', { for: select.id }, t('support.game')), select), button);
  root.replaceChildren(title, h('p', {}, t('support.description')), form, status, list);
  const closed = String(root.dataset.fsSupportClosed ?? '').split(/\s+/).filter(Boolean);
  const controller = createSupportController({ account, storage, closed, onChange: state => {
    root.hidden = !state.visible;
    const selected = select.value;
    const present = [...select.options].map(option => option.value);
    if (JSON.stringify(present) !== JSON.stringify(state.games)) {
      select.replaceChildren(...state.games.map(game => h('option', { value: game }, GAME_NAMES[game] ?? t('support.general'))));
      if (state.games.includes(selected)) select.value = selected;
    }
    select.disabled = state.sending; button.disabled = state.sending || state.loading;
    button.textContent = t(state.sending ? 'support.opening' : state.loggedIn ? 'support.open' : 'support.login');
    status.textContent = state.error ? t(`support.errors.${state.error}`) : state.sending ? t('support.opening') : state.loggedIn && !state.tickets.length ? t('support.empty') : '';
    list.replaceChildren(...state.tickets.map(ticket => h('li', {},
      h('span', {}, `${GAME_NAMES[ticket.game] ?? t('support.general')} · ${ticket.reference} · ${t(`support.states.${ticket.state}`)}`),
      h('a', { href: ticket.url, target: '_blank', rel: 'noopener noreferrer', class: 'fs-btn fs-btn--secondary', 'aria-label': t('support.ticket_link', { number: ticket.reference }) }, t('support.continue')))));
    list.hidden = state.tickets.length === 0;
  } });
  document.addEventListener('fs:account', () => void controller.refresh());
  document.addEventListener('fs:session', () => void controller.refresh());
  void controller.refresh();
  return controller;
}
if (typeof document !== 'undefined') {
  const mount = () => document.querySelectorAll('[data-fs-support]').forEach(root => mountSupport(root, window.FSAccount));
  mount(); document.addEventListener('shopify:section:load', mount);
}
